import React, { useEffect, useState } from 'react';

import useRouter from 'app-migration/hooks/useRouter';

import { FundMethods } from 'stores/ui/registration/registration';
import config from 'utils/config';
import { noop } from 'utils/noop';

import { useStores } from './use-stores';

interface useInitiaiteDepositFlowProps {
    setLoading?: (loading: boolean) => void;
    setIsDepositPopoverVisible?: React.Dispatch<React.SetStateAction<boolean>>;
}

const useInitiateBankDepositFlow = ({
    setLoading = noop,
    setIsDepositPopoverVisible,
}: useInitiaiteDepositFlowProps) => {
    const { uiStore, traderStore, exchangeStore, plaidStore } = useStores();
    const [connectedBankAccount, setConnectedBankAccount] = useState(false);
    const router = useRouter();

    const userStore = traderStore.user;
    const registrationStore = uiStore.registration;
    const userId = userStore.getUserId;
    const bankStore = traderStore.bank;
    const numberOfActiveAccountsSecondary = bankStore.numberOfActiveAccounts;

    useEffect(() => {
        // If the component is unmounted and sync account linking is not enabled
        if (!config.enableSyncAccountLinking && connectedBankAccount) {
            return (): void => {
                // Then we have to make sure we disable polling before unmounting this component
                // So we don't poll for bank accounts the rest of the user session
                bankStore.stopPollingAccounts();
            };
        } // TODO: check or fix hooks error
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [connectedBankAccount, bankStore]);

    const depositDialog = uiStore.depositDialog;
    const plaidLoading = uiStore.plaidLoadingDialog;
    const bankLinkingDialog = uiStore.bankLinkingDialog;

    useEffect(
        () => {
            if (depositDialog.isOpen) {
                plaidStore.load();
            }
        }, // TODO: check or fix hooks error
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [depositDialog.isOpen],
    );

    const linkAccount = (isSignup: boolean = false): void => {
        setLoading(true);
        uiStore.heapTrack('LinkAccountAttempt', { email: userStore.user.email });

        const activeExchangeStore = exchangeStore;

        if (!activeExchangeStore.bankingActive) {
            let details = '. Please try again later.';
            if (!activeExchangeStore.trading_active) {
                details = ', because the exchange is paused. Please try again later.';
            }

            uiStore.pushMessage({
                text: `You cannot link new accounts now${details}`,
                variant: 'neutral',
            });

            setLoading(false);
            return;
        }

        depositDialog.close();
        if (uiStore.featureFlags.enableAeropay) {
            bankLinkingDialog.open();
            bankStore.addAeropayAccount(
                userId,
                () => {
                    setLoading(false);
                    if (!isSignup) {
                        registrationStore.setFundMethod(FundMethods.BANK);
                        depositDialog.open();
                    }
                },
                () => {
                    setLoading(false);
                },
            );
        } else {
            plaidLoading.open();
            bankStore.addAccount(userId, undefined, isSignup, setConnectedBankAccount, noop, (): void => {
                userStore.properties.setCompletedLinkingOnboarding(true);
                router.push('/sign-up/survey');
            });
        }
    };

    const linkAccountButton = (): void => {
        if (numberOfActiveAccountsSecondary === 0) {
            depositDialog.open();
        } else {
            linkAccount();
        }
    };

    const onInitiateBankDepositFlow = (isSignup: boolean = false): void => {
        registrationStore.setFundMethod(FundMethods.BANK);

        const nolinkedAccounts = numberOfActiveAccountsSecondary === 0;
        if (nolinkedAccounts) {
            if (isSignup) {
                userStore.metrics.recordSignUpMetric({
                    step: 'loadingBankAccount',
                });
            }
            linkAccount(isSignup);
            if (setIsDepositPopoverVisible) {
                setIsDepositPopoverVisible(false);
            }
        }
    };

    return { onInitiateBankDepositFlow, linkAccountButton };
};

export default useInitiateBankDepositFlow;
